import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from '../../../hooks/use-debounce-callback';
import { useInterval } from '../../../hooks/use-interval';
import { useInView } from '../../../hooks/useInView';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';

interface UseCarouselOptions<T> {
  /**
   * Unique identifier for the carousel.
   */
  id: string;
  slides: T[];
  interval: number | null;
  initialIndex?: number;
}

interface CarouselCacheEntry {
  status: 'paused' | 'frozen' | 'running';
  activeIndex: number;
}

const cache = new Map<string, CarouselCacheEntry>();

export function useCarousel<T>({ id, slides, interval, initialIndex = 0 }: UseCarouselOptions<T>) {
  const prefersReducesMotion = usePrefersReducedMotion();
  const controlsRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const entry = useMemo(() => cache.get(id), [id]);

  const { activeIndex, activeSlide, go, next, prev } = useCarouselProps({
    slides,
    controlsRef,
    initialIndex: entry ? entry.activeIndex : initialIndex,
  });

  const { pause, resume, freeze, unfreeze, status } = useIntervalProps({
    interval,
    prefersReducesMotion,
    next,
    slides,
    activeIndex,
    controlsRef,
  });

  const { ref: containerRef, inView } = useInView<HTMLDivElement>({ threshold: 0.5 });

  useEffect(() => {
    if (entry === undefined) {
      return;
    }

    if (entry.status === 'frozen') {
      freeze();
    }

    if (entry.status === 'paused') {
      pause();
    }
  }, [entry, freeze, pause]);

  useEffect(() => {
    cache.set(id, { status, activeIndex });
  }, [id, status, activeIndex]);

  useLayoutEffect(() => {
    if (inView) {
      resume();
    } else {
      pause();
    }
  }, [inView, pause, resume]);

  const getContainerProps = useCallback(
    (label: string) => {
      return {
        ref: containerRef,
        'aria-label': label,
        'aria-roledescription': 'carousel',
      } as const;
    },
    [containerRef],
  );

  const getControlsProps = useCallback(() => {
    return {
      ref: controlsRef,
      onFocus: freeze,
      role: 'tablist',
      'aria-label': t('carousel:controls-choose'),
      'aria-live': status === 'running' ? 'off' : 'polite',
    } as const;
  }, [freeze, status, t]);

  const getPickerProps = useCarouselPickerProps({ slides, activeIndex, controlsRef, go, next, prev, pause, resume });

  const getActiveSlideProps = useCallback(() => {
    return {
      role: 'tabpanel',
      'aria-roledescription': 'slide',
      'aria-label': `Slide ${activeIndex + 1} of ${slides.length}`,
    };
  }, [activeIndex, slides.length]);

  return {
    activeSlide,
    activeIndex,
    go,
    next,
    prev,
    pause,
    resume,
    freeze,
    unfreeze,
    status,
    getContainerProps,
    getControlsProps,
    getPickerProps,
    getActiveSlideProps,
  };
}

interface UseIntervalPropsOptions {
  interval: number | null;
  prefersReducesMotion: boolean;
  next: () => void;
  slides: unknown[];
  activeIndex: number;
  controlsRef: React.RefObject<HTMLDivElement>;
}

function getStatus(interval: number | null, frozen: boolean) {
  if (interval === null) {
    return 'paused';
  }

  return frozen ? 'frozen' : 'running';
}

function useIntervalProps({
  interval: intervalInit,
  prefersReducesMotion,
  next,
  slides,
  activeIndex,
  controlsRef,
}: UseIntervalPropsOptions) {
  const [interval, setInterval] = useState<number | null>(prefersReducesMotion ? null : intervalInit);
  const [frozen, setFrozen] = useState(false);
  const pause = useCallback(() => setInterval(null), []);
  const resume = useCallback(() => setInterval(intervalInit), [intervalInit]);
  const freeze = useCallback(() => setFrozen(true), []);
  const unfreeze = useCallback(() => setFrozen(false), []);
  const status = getStatus(interval, frozen);

  useInterval(() => next(), frozen ? null : interval);

  useLayoutEffect(() => {
    const controls = controlsRef.current;

    if (controls) {
      const laneWidth = controls.scrollWidth;
      const screenWidth = window.innerWidth;
      const step = (laneWidth - screenWidth) / (slides.length - 1);
      const margin = 40 / (slides.length - 1);
      const scrollBy = (step + margin) * activeIndex;
      controls.style.setProperty('transform', `translateX(-${scrollBy}px)`);
    }
  }, [activeIndex, controlsRef, slides.length]);

  return {
    pause,
    resume,
    freeze,
    unfreeze,
    status,
  } as const;
}

interface UseCarouselPropsOptions<T> {
  slides: T[];
  controlsRef: React.RefObject<HTMLDivElement>;
  initialIndex?: number;
}

function useCarouselProps<T>({ slides, controlsRef, initialIndex = 0 }: UseCarouselPropsOptions<T>) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  const focusIndex = useCallback(
    (index: number) => {
      const controls = controlsRef.current;
      if (controls) {
        const picker = controls.children[index];
        if (picker instanceof HTMLElement) {
          picker.focus();
        }
      }
    },
    [controlsRef],
  );

  const go = useCallback(
    (x: number) => {
      const idx = (x + slides.length) % slides.length;
      setActiveIndex(idx);
    },
    [slides.length],
  );

  const next = useCallback(
    (focus?: boolean) => {
      setActiveIndex((x) => {
        const idx = (x + 1) % slides.length;

        if (focus) {
          focusIndex(idx);
        }

        return idx;
      });
    },
    [focusIndex, slides.length],
  );

  const prev = useCallback(
    (focus?: boolean) => {
      setActiveIndex((x) => {
        const idx = (x - 1 + slides.length) % slides.length;

        if (focus) {
          focusIndex(idx);
        }

        return idx;
      });
    },
    [focusIndex, slides.length],
  );

  const activeSlide = useMemo(() => slides[activeIndex], [slides, activeIndex]);

  return {
    activeSlide,
    activeIndex,
    go,
    next,
    prev,
  };
}

interface CarouselPickerProps {
  slides: unknown[];
  activeIndex: number;
  controlsRef: React.RefObject<HTMLDivElement>;
  go: (index: number) => void;
  next: (focus?: boolean) => void;
  prev: (focus?: boolean) => void;
  pause: () => void;
  resume: () => void;
}

function useCarouselPickerProps({
  slides,
  activeIndex,
  controlsRef,
  go,
  next,
  prev,
  pause,
  resume,
}: CarouselPickerProps) {
  const { t } = useTranslation();
  const debouncedResume = useDebounceCallback(resume, 1000);

  const isMouseDown = useRef(false);

  const getPickerIndex = useCallback(
    (element: Element) => {
      const controls = controlsRef.current;

      if (controls) {
        const pickerIndex = Array.from(controls.children).indexOf(element);
        return pickerIndex >= 0 ? pickerIndex : undefined;
      }
    },
    [controlsRef],
  );

  return useCallback(
    (index: number) => {
      const isActive = index === activeIndex;
      return {
        'aria-labelledby': t('carousel:controls-choose'),
        'aria-label': t('carousel:controls-display', { index: index + 1, total: slides.length }),
        'aria-selected': isActive,
        tabIndex: isActive ? 0 : -1,
        role: 'tab',
        onClick: () => go(index),
        onMouseDown: () => {
          isMouseDown.current = true;
        },
        onMouseUp: () => {
          isMouseDown.current = false;
        },
        onMouseEnter: () => {
          debouncedResume.cancel();
          pause();
        },
        onMouseLeave: () => debouncedResume(),
        onFocus: (event: React.FocusEvent) => {
          const idx = getPickerIndex(event.target);
          if (idx !== undefined && !isMouseDown.current) {
            go(idx);
          }
        },
        onKeyDown: (event: React.KeyboardEvent) => {
          switch (event.key) {
            case 'ArrowRight':
              next(true);
              break;
            case 'ArrowLeft':
              prev(true);
              break;
            case 'Home':
              go(0);
              break;
            case 'End':
              go(-1);
              break;
          }
        },
      } as const;
    },
    [activeIndex, debouncedResume, getPickerIndex, go, next, pause, prev, slides.length, t],
  );
}
